import { Component, OnInit, OnChanges, Input, Output, EventEmitter, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { HttpEventType, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Capacitor } from '@capacitor/core';
import { Storage } from '@ionic/storage-angular';
import { FileService } from './file.service';
import { IFile } from '../../models/file.models';

// Clave de Storage bajo la que se guarda un registro mínimo de "hay una subida de fichero en
// curso" justo antes de abrir el selector nativo de Android. Exportada para que AppComponent
// pueda leerla al relanzar la app (ver checkPendingFileUpload en app.component.ts).
export const PENDING_FILE_UPLOAD_KEY = 'PENDING_FILE_UPLOAD';

@Component({
  selector: 'fun-file',
  templateUrl: './file.component.html',
  styleUrls: ['./file.component.scss']
})
export class FileComponent implements OnInit, OnChanges {

  @Input() buttonType: string;
  @Input() buttonTitle: string;
  @Input() buttonAccept: string;
  @Input() urlFile: string;
  @Input() paramsFile: {};
  @Input() showProgress = true;
  @Input() token = '';
  // Datos mínimos (p.ej. { type: 'bid', id }) para poder recuperar el contexto si Android mata
  // el proceso mientras el selector de archivos está abierto (ver onClick/PENDING_FILE_UPLOAD_KEY).
  @Input() androidResumeData?: any;
  @Output() private eventUploadResponse = new EventEmitter<HttpResponse<any> | HttpErrorResponse>();

  isDisabled = false;
  prevButtonType = '';

  @ViewChild('fileUpload', {static: false}) fileUpload: ElementRef;
  public file: IFile;

  constructor(
    private fileService: FileService,
    private chdRef: ChangeDetectorRef,
    private storage: Storage
  ) { }

  ngOnInit() {
    this.prevButtonType = this.buttonType;
  }

  ngOnChanges() {
    if (this.buttonType === 'disabled' || this.buttonType === 'secondary-disabled') {
      this.isDisabled = true;
    } else {
      this.isDisabled = false;
    }
  }

  onClick() {
    // Android puede matar el proceso de la app mientras el selector nativo de archivos está
    // abierto (process death por backgrounding), perdiendo todo el estado en memoria (incluida la
    // modal desde la que se subía el documento) sin ninguna traza de qué se estaba haciendo.
    // Guardamos aquí lo mínimo para que AppComponent pueda recuperar el contexto al relanzar
    // (ver checkPendingFileUpload en app.component.ts).
    if (Capacitor.getPlatform() === 'android' && this.androidResumeData) {
      this.storage.set(PENDING_FILE_UPLOAD_KEY, this.androidResumeData);
    }
    const fileUpload = this.fileUpload.nativeElement;
    fileUpload.onchange = () => {
      const file = fileUpload.files[0];
      this.fileService.toBase64(file).then(fileBase64 => {
         this.file = { data: fileBase64.split(',')[1], progress: 0 };
         this.chdRef.detectChanges();
         this.uploadFile(this.urlFile, this.file, this.paramsFile);
      });
    };
    fileUpload.click();
  }

  uploadFile(url: string, file: IFile, params?: {}): void {
    const data = file.data;
    params = {...params, file: data};
    this.disableButton();

    this.fileService.upload(url, params, this.token).pipe(
      map(event => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            file.progress = Math.round(event.loaded * 100 / event.total);
            this.chdRef.detectChanges();
            break;
          case HttpEventType.Response:
            return event;
        }
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(error);
      })).subscribe((response: HttpResponse<any> | HttpErrorResponse) => {
        this.clearAndroidResumeFlag();
        if (typeof (response) === 'object') {
          this.emitEventUploadResponse(response);
          this.resetButton(file);
          this.enableButton();
        }
      }, (error: HttpErrorResponse) => {
        this.clearAndroidResumeFlag();
        this.emitEventUploadResponse(error);
        this.resetButton(file);
        this.enableButton();
      });
  }

  private clearAndroidResumeFlag(): void {
    if (Capacitor.getPlatform() === 'android' && this.androidResumeData) {
      this.storage.remove(PENDING_FILE_UPLOAD_KEY);
    }
  }

  enableButton(): void {
    this.buttonType = this.prevButtonType;
    this.isDisabled = false;
    this.chdRef.detectChanges();
  }

  disableButton(): void {
    this.buttonType = 'disabled';
    this.isDisabled = true;
    this.chdRef.detectChanges();
  }

  resetButton(file: IFile): void {
    this.fileUpload.nativeElement.value = '';
    file.progress = 0;
    this.chdRef.detectChanges();
  }

  emitEventUploadResponse(response: HttpResponse<any> | HttpErrorResponse): void {
    this.eventUploadResponse.emit(response);
  }

}
